import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const ABOUT_IMAGE = '/images/vera.jpg';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' },
  viewport: { once: true }
};

const stats = [
  { value: '100%', key: 'dedication' },
  { value: 'Cada', key: 'uniqueSession' },
  { value: '∞', key: 'memoriesForever' },
];

export default function About() {
  const { t } = useLanguage();

  return (
    <div data-testid="about-page" className="pt-24 bg-warm-50">
      {/* Header */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center">
            <span className="text-accent font-medium text-sm tracking-wider uppercase mb-3 block">
              {t.about.subtitle}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-semibold text-warm-900">
              {t.about.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-14 md:py-20 bg-warm-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[3/4] overflow-hidden rounded-2xl shadow-xl">
                <img
                  src={ABOUT_IMAGE}
                  alt="Vera Hilário"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-accent/10 rounded-2xl -z-10" />
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-warm-200 rounded-2xl -z-10" />
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-accent mb-6">
                {t.about.greeting}
              </h2>

              <div className="space-y-5 text-warm-600 leading-relaxed">
                {t.about.story.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 mt-10 pt-10 border-t border-warm-200">
                {stats.map((stat) => (
                  <div key={stat.key} className="text-center">
                    <span className="text-accent text-3xl md:text-4xl font-heading font-bold block mb-1">
                      {stat.value}
                    </span>
                    <span className="text-warm-500 text-xs font-medium uppercase tracking-wider">
                      {t.about[stat.key]}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20 md:py-28 bg-white relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent/5 rounded-full blur-3xl" />

        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div {...fadeUp}>
            <span className="text-accent text-6xl md:text-7xl font-heading">"</span>
            <blockquote className="font-heading text-xl md:text-2xl text-warm-700 leading-relaxed -mt-6">
              {t.footer.tagline}
            </blockquote>
            <p className="text-accent font-medium text-sm tracking-wider uppercase mt-6">
              — Vera Hilário
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
